import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Bell, Phone, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const NotificationBell = () => {
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [callbacks, setCallbacks] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);
  const lastCheckRef = useRef(null);
  const prevCallbackCountRef = useRef(0);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const playSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.4);
    } catch (e) {}
  }, []);

  const playUrgentSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      for (let i = 0; i < 3; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.setValueAtTime(1200, ctx.currentTime + i * 0.2);
        osc.frequency.setValueAtTime(900, ctx.currentTime + i * 0.2 + 0.1);
        gain.gain.setValueAtTime(0.4, ctx.currentTime + i * 0.2);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.2 + 0.18);
        osc.start(ctx.currentTime + i * 0.2);
        osc.stop(ctx.currentTime + i * 0.2 + 0.18);
      }
    } catch (e) {}
  }, []);

  const handleMarkDone = async (callbackId, e) => {
    e.stopPropagation();
    try {
      await api.markCallbackDone(callbackId);
      setCallbacks(prev => prev.filter(c => c.id !== callbackId));
      toast.success('Callback marcado como realizado!');
    } catch { toast.error('Erro ao marcar callback'); }
  };

  const checkNotifications = useCallback(async () => {
    try {
      const since = lastCheckRef.current;
      const results = await Promise.allSettled([
        api.getNewLeads(since),
        api.getCallbacks()
      ]);

      const newLeads = results[0].status === 'fulfilled' ? (results[0].value.data.leads || []) : [];
      const newCallbacks = results[1].status === 'fulfilled' ? (results[1].value.data.callbacks || []) : [];
      const checkedAt = results[0].status === 'fulfilled' ? results[0].value.data.checked_at : null;

      if (since && newLeads.length > recentLeads.length) {
        playSound();
        toast.info(`${newLeads.length - recentLeads.length} novo(s) lead(s)!`, {
          description: newLeads[0]?.name || 'Novo lead recebido',
          action: { label: 'Ver', onClick: () => navigate('/leads') }
        });
      }

      // Notificacao urgente para callbacks novos
      if (newCallbacks.length > prevCallbackCountRef.current) {
        playUrgentSound();
        const newest = newCallbacks[0];
        toast.warning(`LIGAR AGORA: ${newest?.lead_name}`, {
          description: `Tel: ${newest?.lead_phone} - Cliente com interesse!`,
          duration: 15000,
          action: { label: 'Ver Lead', onClick: () => navigate(`/leads/${newest?.lead_id}`) }
        });
      }

      prevCallbackCountRef.current = newCallbacks.length;
      setNewLeadsCount(newLeads.length);
      setCallbacks(newCallbacks);
      setRecentLeads(newLeads);
      lastCheckRef.current = checkedAt;
    } catch (e) {}
  }, [navigate, playSound, playUrgentSound, recentLeads.length]);

  useEffect(() => {
    checkNotifications();
    const interval = setInterval(checkNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalCount = newLeadsCount + callbacks.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative p-2 rounded-lg transition-colors ${callbacks.length > 0 ? 'text-red-600 bg-red-50 animate-pulse' : 'text-slate-600 hover:bg-slate-100'}`}
        data-testid="notification-bell"
      >
        {callbacks.length > 0 ? <PhoneCall className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        {totalCount > 0 && (
          <span className={`absolute -top-0.5 -right-0.5 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${callbacks.length > 0 ? 'bg-red-600 animate-bounce' : 'bg-red-500 animate-pulse'}`}>
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          {/* Callbacks urgentes */}
          {callbacks.length > 0 && (
            <>
              <div className="p-3 border-b bg-red-50">
                <h3 className="text-sm font-bold text-red-800 flex items-center gap-1.5">
                  <PhoneCall className="w-4 h-4" /> Ligar Agora ({callbacks.length})
                </h3>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {callbacks.map((cb) => (
                  <div
                    key={cb.id}
                    className="p-3 border-b border-red-100 bg-red-50/50 hover:bg-red-100 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => { navigate(`/leads/${cb.lead_id}`); setShowDropdown(false); }}
                        className="flex-1 text-left"
                        data-testid={`callback-${cb.id}`}
                      >
                        <p className="text-sm font-bold text-red-800">{cb.lead_name}</p>
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" /> {cb.lead_phone}
                        </p>
                        <p className="text-[11px] text-red-500 mt-0.5">Cliente com interesse - ligar agora!</p>
                      </button>
                      <button
                        onClick={(e) => handleMarkDone(cb.id, e)}
                        className="shrink-0 px-2 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                        data-testid={`callback-done-${cb.id}`}
                      >
                        Feito
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Leads novos */}
          <div className="p-3 border-b bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-800">Leads Novos ({newLeadsCount})</h3>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {recentLeads.length === 0 && callbacks.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-400">
                Nenhuma notificacao pendente
              </div>
            )}
            {recentLeads.map((lead, i) => (
              <button
                key={`lead-${i}`}
                onClick={() => { navigate(`/leads/${lead.id}`); setShowDropdown(false); }}
                className="w-full p-3 text-left hover:bg-emerald-50 border-b border-slate-100 transition-colors"
                data-testid={`notification-lead-${lead.id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">Novo lead: {lead.name}</p>
                    <p className="text-xs text-slate-500">{lead.phone} - {lead.source}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
